/**
 * Navigation Configuration
 * AIDLC Administrative Backend
 *
 * Defines the complete navigation structure with persona-based visibility.
 * Each nav section contains items, and each item specifies which personas
 * can see it. The Sidebar component filters based on the active persona.
 *
 * Persona visibility key:
 *   executive   - CEO / Customer
 *   ops         - Operations Director (Dan) -- sees EVERYTHING
 *   orchestrator - Scrum Master (Sam)
 *   builder     - Developer (Marcus etc)
 *   gatekeeper  - Reviewer (Bill, Asha)
 */

import type { Persona } from '../../types/persona';

export interface NavItem {
  path: string;
  label: string;
  icon: string;
  /** Which personas can see this item. If omitted, all personas see it. */
  personas?: Persona[];
  /** Whether to use 'end' matching (exact path). Defaults to false. */
  exact?: boolean;
}

export interface NavSection {
  id: string;
  label: string;
  icon: string;
  /** Which personas can see this entire section. If omitted, all personas see it. */
  personas?: Persona[];
  items: NavItem[];
}

/**
 * Complete navigation structure grouped into logical sections.
 *
 * "ops" persona is intentionally included in every section/item so that
 * the Operations Director always has full visibility.
 */
export const NAV_SECTIONS: NavSection[] = [
  // ─── Command Center ───────────────────────────────────────────
  {
    id: 'command-center',
    label: 'Command Center',
    icon: 'command',
    // Visible to all personas
    items: [
      {
        path: '/',
        label: 'Dashboard',
        icon: 'grid',
        exact: true,
      },
      {
        path: '/training',
        label: 'Training',
        icon: 'file',
      },
    ],
  },

  // ─── Planning & Budget ────────────────────────────────────────
  {
    id: 'planning-budget',
    label: 'Planning & Budget',
    icon: 'dollar',
    personas: ['executive', 'ops', 'orchestrator'],
    items: [
      {
        path: '/proposals',
        label: 'Budget Proposals',
        icon: 'proposal',
        personas: ['executive', 'ops'],
      },
      {
        path: '/roadmap',
        label: 'Token Roadmap',
        icon: 'roadmap',
        personas: ['executive', 'ops', 'orchestrator'],
      },
      {
        path: '/milestones',
        label: 'Milestone Tracker',
        icon: 'flag',
        personas: ['executive', 'ops', 'orchestrator'],
      },
    ],
  },

  // ─── Orchestration ────────────────────────────────────────────
  {
    id: 'orchestration',
    label: 'Orchestration',
    icon: 'layers',
    personas: ['executive', 'ops', 'orchestrator'],
    items: [
      {
        path: '/spawn-planner',
        label: 'Spawn Planner',
        icon: 'bolt',
        personas: ['ops', 'orchestrator'],
      },
      {
        path: '/scheduler',
        label: 'Agent Scheduler',
        icon: 'calendar',
        personas: ['ops', 'orchestrator'],
      },
      {
        path: '/assessments',
        label: 'Assessment Reports',
        icon: 'clipboard',
        personas: ['ops', 'orchestrator'],
      },
      {
        path: '/recurring-tasks',
        label: 'Recurring Tasks',
        icon: 'clock',
        personas: ['executive', 'ops'],
      },
    ],
  },

  // ─── Work Management ──────────────────────────────────────────
  {
    id: 'work-management',
    label: 'Work Management',
    icon: 'list',
    // Visible to all personas
    items: [
      {
        path: '/projects',
        label: 'Sprint Board',
        icon: 'kanban',
      },
      {
        path: '/backlog',
        label: 'Backlog',
        icon: 'list',
      },
      {
        path: '/features',
        label: 'Feature Tracker',
        icon: 'layers',
      },
      {
        path: '/lifecycle',
        label: 'AIDLC Lifecycle',
        icon: 'workflow',
      },
    ],
  },

  // ─── Quality & Review ─────────────────────────────────────────
  {
    id: 'quality-review',
    label: 'Quality & Review',
    icon: 'check-shield',
    personas: ['executive', 'ops', 'orchestrator', 'gatekeeper'],
    items: [
      {
        path: '/dod-pipeline',
        label: 'DoD Pipeline',
        icon: 'check-shield',
        personas: ['ops', 'orchestrator', 'gatekeeper'],
      },
      {
        path: '/escalations',
        label: 'Escalation Queue',
        icon: 'alert',
        personas: ['executive', 'ops', 'orchestrator', 'gatekeeper'],
      },
      {
        path: '/review-reports',
        label: 'Review Reports',
        icon: 'clipboard',
        personas: ['executive', 'ops', 'gatekeeper'],
      },
      {
        path: '/qa-dashboard',
        label: 'QA Dashboard',
        icon: 'activity',
        personas: ['executive', 'ops', 'gatekeeper'],
      },
    ],
  },

  // ─── Monitoring ───────────────────────────────────────────────
  {
    id: 'monitoring',
    label: 'Monitoring',
    icon: 'activity',
    personas: ['executive', 'ops', 'orchestrator'],
    items: [
      {
        path: '/burn-monitor',
        label: 'Budget Burn',
        icon: 'fire',
        personas: ['executive', 'ops', 'orchestrator'],
      },
      {
        path: '/tokens',
        label: 'Token Usage',
        icon: 'coins',
        personas: ['executive', 'ops', 'orchestrator'],
      },
      {
        path: '/audit',
        label: 'Audit Log',
        icon: 'clock',
        personas: ['ops', 'orchestrator'],
      },
    ],
  },

  // ─── Documentation ───────────────────────────────────────────
  {
    id: 'documentation',
    label: 'Documentation',
    icon: 'book',
    // Visible to all personas
    items: [
      {
        path: '/docs',
        label: 'Documentation Hub',
        icon: 'book',
        exact: true,
      },
      {
        path: '/deployment-guide',
        label: 'Deploy Guide',
        icon: 'rocket',
      },
      {
        path: '/docs/email',
        label: 'Email Guide',
        icon: 'mail',
      },
      {
        path: '/docs/user-guide',
        label: 'User Guide',
        icon: 'file',
      },
    ],
  },

  // ─── Team & Admin ─────────────────────────────────────────────
  {
    id: 'team-admin',
    label: 'Team & Admin',
    icon: 'settings',
    personas: ['ops'],
    items: [
      {
        path: '/hiring',
        label: 'Hiring Pipeline',
        icon: 'user-plus',
        personas: ['ops'],
      },
      {
        path: '/governance',
        label: 'Governance',
        icon: 'shield',
        personas: ['ops'],
      },
      {
        path: '/control',
        label: 'Control Room',
        icon: 'terminal',
        personas: ['ops'],
      },
      {
        path: '/deployment',
        label: 'Deployment',
        icon: 'rocket',
        personas: ['ops'],
      },
      {
        path: '/templates',
        label: 'SOPs',
        icon: 'file',
        personas: ['ops'],
      },
      {
        path: '/email',
        label: 'Email',
        icon: 'mail',
        personas: ['ops'],
      },
      {
        path: '/chat',
        label: 'Chat Interface',
        icon: 'message',
        personas: ['ops'],
      },
      {
        path: '/secrets',
        label: 'Secrets Inventory',
        icon: 'key',
        personas: ['ops'],
      },
      {
        path: '/settings',
        label: 'Settings',
        icon: 'settings',
        personas: ['ops'],
      },
    ],
  },
];

/**
 * Filter sections and items based on the active persona.
 * The "ops" persona always sees everything regardless of persona arrays.
 */
export function getVisibleSections(persona: Persona): NavSection[] {
  // Ops sees everything
  if (persona === 'ops') {
    return NAV_SECTIONS;
  }

  return NAV_SECTIONS
    .filter((section) => {
      // If section has no persona restriction, everyone sees it
      if (!section.personas) return true;
      return section.personas.includes(persona);
    })
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        // If item has no persona restriction, everyone sees it
        if (!item.personas) return true;
        return item.personas.includes(persona);
      }),
    }))
    .filter((section) => section.items.length > 0);
}
